import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddNoteDialogComponent } from '@shared/components/add-note-dialog/add-note-dialog.component';
import { ItemModel } from '@shared/models/item.model';
import { MenuCategoryModel } from '@shared/models/menu-category.model';
import { MenuItemModel } from '@shared/models/menu-item.model';
import { OfferModel } from '@shared/models/offer.model';
import { PageData } from '@shared/models/pagedata.model';
import { CartService } from '@shared/services/cart.service';
import { LanguageService } from '@shared/services/language.service';
import { MenuCategoriesService } from '@shared/services/menuCategories/menu-categories.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';

export interface OrderMenuDialogData {
  title?: string;
  subtitle?: string;
  tableId?: string | null;
  orderId?: string | null;
  isItemsToOrder?: boolean;
  returnUrl?: string | null;
  existingOrderItems?: ItemModel[];
}

interface SelectedOrderMenuItem {
  item: MenuItemModel;
  quantity: number;
  existingQuantity: number;
  newQuantity: number;
  notes?: string;
  total: number;
}

@Component({
  selector: 'app-order-menu-dialog',
  templateUrl: './order-menu-dialog.component.html',
  styleUrls: ['./order-menu-dialog.component.scss'],
  standalone: false,
})
export class OrderMenuDialogComponent implements OnInit, OnDestroy {
  loading = true;
  filterKeyword: string | null = null;
  searchTerm = '';
  menuCategories: MenuCategoryModel[] = [];
  categoryTitle = '';
  activeCategoryId = '';
  pageData: PageData = { pageIndex: 0, pageSize: 50, totalRows: 0 };
  currentSort: string | null = null;

  dialogTitle = 'Add order';
  dialogSubtitle = 'Choose items from the menu';
  tableId: string | null = null;
  orderId: string | null = null;
  returnUrl: string | null = null;
  isItemsToOrder = false;
  isCartExpanded = false;
  existingOrderItems: ItemModel[] = [];
  private cartDragStartY: number | null = null;
  private cartSubscription?: Subscription;

  constructor(
    private spinnerService: NgxSpinnerService,
    private menuCategoryService: MenuCategoriesService,
    public cartService: CartService,
    public language: LanguageService,
    public dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: OrderMenuDialogData | null,
    @Optional() private dialogRef: MatDialogRef<OrderMenuDialogComponent>
  ) {
    this.dialogTitle = dialogData?.title || this.dialogTitle;
    this.dialogSubtitle = dialogData?.subtitle || this.dialogSubtitle;
    this.tableId = dialogData?.tableId || null;
    this.orderId = dialogData?.orderId || null;
    this.returnUrl = dialogData?.returnUrl || null;
    this.isItemsToOrder = !!dialogData?.isItemsToOrder;
    this.existingOrderItems = dialogData?.existingOrderItems || [];
  }

  ngOnInit() {
    this.loadData();
    this.cartService.getCartProduct();
    this.cartSubscription = this.cartService.cartProducts$.subscribe(() => {
      this.cartService.updateCartQuantity();
      this.cartService.updateTotalPrice();
    });
  }

  ngOnDestroy() {
    this.cartSubscription?.unsubscribe();
  }

  filter(searchKeyword: string) {
    this.searchTerm = searchKeyword || '';
  }

  loadData() {
    this.spinnerService.show().then();
    this.menuCategoryService
      .getAll(this.pageData.pageIndex + 1, this.pageData.pageSize, this.currentSort, this.filterKeyword, null)
      .subscribe({
        next: value => {
          this.menuCategories = this.withOffersCategory(this.readMenuCategories(value.data), value.offers ?? []);
          this.categoryTitle = this.menuCategories?.[0]?.title || '';
          this.activeCategoryId = this.menuCategories?.[0]?.id || '';
          this.loading = false;
          this.spinnerService.hide().then();
        },
        error: () => {
          this.loading = false;
          this.spinnerService.hide().then();
        },
      });
  }

  private readMenuCategories(data: MenuCategoryModel[] | { categories?: MenuCategoryModel[] } | null | undefined): MenuCategoryModel[] {
    return Array.isArray(data) ? data : data?.categories ?? [];
  }

  private withOffersCategory(categories: MenuCategoryModel[], offers: OfferModel[]): MenuCategoryModel[] {
    const activeOffers = offers.filter(offer => offer.is_active !== false);
    if (!activeOffers.length) {
      return categories;
    }

    return [this.createOffersCategory(activeOffers), ...categories];
  }

  private createOffersCategory(offers: OfferModel[]): MenuCategoryModel {
    return {
      id: 'offers',
      title: 'Offers',
      title_ar: 'العروض',
      description: 'Available offers',
      description_ar: 'العروض المتاحة',
      image: '',
      menu_items: offers.map(offer => this.offerToMenuItem(offer)) as [],
      menu_category_title: 'Offers',
      menu_category_id: 'offers',
    };
  }

  private offerToMenuItem(offer: OfferModel): MenuItemModel {
    return {
      id: offer.order_menu_item_id || offer.id,
      menu_category_title: 'Offers',
      menu_category_id: 'offers',
      menu_title: '',
      available_quantity: 1,
      title: offer.title,
      title_ar: offer.title_ar,
      description: offer.description,
      description_ar: offer.description_ar,
      image: offer.image || '',
      price: offer.price,
      cost: 0,
      currency: offer.currency,
      status: { key: 1, value: 'ACTIVE' },
      menu_category: null,
      notes: '',
      sub_items: [],
      type_label: 'Offer',
    } as MenuItemModel;
  }

  selectCategory(category: MenuCategoryModel) {
    this.activeCategoryId = category.id;
    this.categoryTitle = category.title;
    const categoryElement = document.getElementById(this.categoryDomId(category.id));
    categoryElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  categoryItems(category: MenuCategoryModel): MenuItemModel[] {
    return (category.menu_items || []) as MenuItemModel[];
  }

  filteredCategoryItems(category: MenuCategoryModel): MenuItemModel[] {
    const items = this.categoryItems(category);
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return items;
    }

    return items.filter(item => {
      const searchable = [
        item.title,
        item.title_ar,
        item.description,
        item.description_ar,
        item.type_label,
        category.title,
        category.title_ar,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(term);
    });
  }

  get visibleCategories() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.menuCategories;
    }

    return this.menuCategories.filter(category => this.filteredCategoryItems(category).length > 0);
  }

  get allItems(): MenuItemModel[] {
    return this.menuCategories.flatMap(category => this.categoryItems(category));
  }

  get selectedItems(): SelectedOrderMenuItem[] {
    const cartItems = this.readCartItems();
    const itemById = new Map(this.allItems.map(item => [item.id, item]));
    const grouped = new Map<string, SelectedOrderMenuItem>();

    cartItems.forEach(cartItem => {
      const item = itemById.get(cartItem.id);
      if (!item) {
        return;
      }
      const key = `${cartItem.id}-${cartItem.notes || ''}`;
      const current = grouped.get(key);
      if (current) {
        current.quantity++;
        current.newQuantity++;
        current.total += cartItem.price;
        return;
      }
      grouped.set(key, {
        item,
        quantity: 1,
        existingQuantity: 0,
        newQuantity: 1,
        notes: cartItem.notes,
        total: cartItem.price,
      });
    });

    this.existingOrderItems.forEach(orderItem => {
      const item = this.toMenuItem(orderItem);
      const key = `${item.id}-${orderItem.notes || ''}`;
      const current = grouped.get(key);
      if (current) {
        current.quantity++;
        current.existingQuantity++;
        current.total += item.price;
        return;
      }
      grouped.set(key, {
        item,
        quantity: 1,
        existingQuantity: 1,
        newQuantity: 0,
        notes: orderItem.notes,
        total: item.price,
      });
    });

    return Array.from(grouped.values());
  }

  get displayCartQuantity() {
    return this.selectedItems.reduce((total, selected) => total + selected.quantity, 0);
  }

  get displayCartTotal() {
    return this.selectedItems.reduce((total, selected) => total + selected.total, 0);
  }

  addItem(item: MenuItemModel) {
    this.cartService.addToCart(item, null);
  }

  removeItem(itemId: string) {
    this.cartService.removeFromCart(itemId);
  }

  removeSelectedSingle(selected: SelectedOrderMenuItem) {
    if (selected.newQuantity < 1) {
      return;
    }

    const cartItems = this.readCartItems();
    const normalizedNotes = selected.notes || '';
    const selectedKey = `${selected.item.id}-${normalizedNotes}`;
    const index = cartItems.map(item => `${item.id}-${item.notes || ''}`).lastIndexOf(selectedKey);

    if (index > -1) {
      cartItems.splice(index, 1);
      this.saveCartItems(cartItems);
    }
  }

  removeSelectedItem(event: Event, selected: SelectedOrderMenuItem) {
    event.preventDefault();
    event.stopPropagation();
    if (selected.newQuantity < 1) {
      return;
    }

    const normalizedNotes = selected.notes || '';
    const nextCart = this.readCartItems().filter(item => {
      const sameItem = String(item.id) === String(selected.item.id);
      const sameNotes = (item.notes || '') === normalizedNotes;
      return !(sameItem && sameNotes);
    });

    this.saveCartItems(nextCart);
  }

  addItemWithNote(item: MenuItemModel) {
    const noteDialog = this.dialog.open(AddNoteDialogComponent, {
      width: '380px',
      data: { item },
    });

    noteDialog.afterClosed().subscribe(() => {
      this.cartService.updateCartQuantity();
      this.cartService.updateTotalPrice();
    });
  }

  itemCount(itemId: string) {
    return this.cartService.countItem(itemId) + this.existingItemCount(itemId);
  }

  newItemCount(itemId: string) {
    return this.cartService.countItem(itemId);
  }

  isUnavailable(item: MenuItemModel) {
    return Number(item.available_quantity) === 0 || item.status?.key === 2 || item.status?.value === 'INACTIVE';
  }

  displayTitle(item: MenuItemModel | MenuCategoryModel) {
    return this.language.isArabic ? item.title_ar || item.title : item.title;
  }

  displayDescription(item: MenuItemModel | MenuCategoryModel) {
    return this.language.isArabic ? item.description_ar || item.description : item.description;
  }

  categoryDomId(categoryId: string) {
    return `order-menu-category-${categoryId}`;
  }

  trackCategory(_: number, category: MenuCategoryModel) {
    return category.id;
  }

  trackItem(_: number, item: MenuItemModel) {
    return item.id;
  }

  changeSource(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/images/image-upload.svg';
  }

  closeDialog() {
    this.dialogRef?.close();
  }

  onOrderCompleted(orderId: string) {
    this.dialogRef?.close({ orderId });
  }

  toggleCart() {
    this.isCartExpanded = !this.isCartExpanded;
  }

  startCartDrag(event: TouchEvent | PointerEvent) {
    this.cartDragStartY = this.getClientY(event);
  }

  finishCartDrag(event: TouchEvent | PointerEvent) {
    if (this.cartDragStartY === null) {
      return;
    }

    const dragDistance = this.getClientY(event) - this.cartDragStartY;
    if (dragDistance < -28) {
      this.isCartExpanded = true;
    }
    if (dragDistance > 28) {
      this.isCartExpanded = false;
    }
    this.cartDragStartY = null;
  }

  private getClientY(event: TouchEvent | PointerEvent): number {
    if ('changedTouches' in event && event.changedTouches.length) {
      return event.changedTouches[0].clientY;
    }
    if ('touches' in event && event.touches.length) {
      return event.touches[0].clientY;
    }
    return (event as PointerEvent).clientY;
  }

  private readCartItems(): { id: string; price: number; notes?: string }[] {
    const cartData = localStorage.getItem('cart');
    if (!cartData) {
      return [];
    }

    try {
      const parsed = JSON.parse(cartData);
      if (Array.isArray(parsed)) {
        return parsed as { id: string; price: number; notes?: string }[];
      }
    } catch (error) {
      console.warn('Invalid cart data found in localStorage', error);
    }

    localStorage.removeItem('cart');
    localStorage.removeItem('cartTimestamp');
    return [];
  }

  private saveCartItems(cartItems: { id: string; price: number; notes?: string }[]) {
    if (cartItems.length) {
      localStorage.setItem('cart', JSON.stringify(cartItems));
      localStorage.setItem('cartTimestamp', new Date().getTime().toString());
    } else {
      localStorage.removeItem('cart');
      localStorage.removeItem('cartTimestamp');
    }

    this.cartService.getCartProduct();
  }

  private existingItemCount(itemId: string) {
    return this.existingOrderItems.filter(orderItem => String(orderItem.menuItem.id) === String(itemId)).length;
  }

  private toMenuItem(orderItem: ItemModel): MenuItemModel {
    return {
      id: orderItem.menuItem.id,
      menu_category_title: '',
      menu_category_id: '',
      menu_title: '',
      available_quantity: 1,
      title: orderItem.menuItem.title,
      title_ar: '',
      description: '',
      description_ar: '',
      image: orderItem.menuItem.image,
      price: orderItem.menuItem.price,
      cost: 0,
      currency: orderItem.menuItem.currency,
      status: orderItem.menuItem.status,
      menu_category: null,
      notes: orderItem.notes,
      sub_items: [],
      type_label: '',
    } as MenuItemModel;
  }
}
